"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import { PORTAL_MAX_WIDTH, portalFont } from "../partnerTheme";
import { dkStadiumConfig } from "../campaigns/draft-kings-stadium";

const ACCENT = dkStadiumConfig.accentColor;
const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.62)";
const CARD_BG = "rgba(255,255,255,0.04)";
const RADIUS = "16px";
const WAITLIST_BASE = 39394;
const IMG = dkStadiumConfig.images.talent;

const NAV = [
  { id: "who-we-are", label: "Who we are" },
  { id: "how-it-works", label: "How a night works" },
  { id: "demand", label: "Our Waitlist" },
  { id: "demographics", label: "Demographics" },
  { id: "press", label: "Press" },
  { id: "partners", label: "Partners" },
] as const;

const PARTNER_LOGO_ASSETS = {
  verizon: "https://storage.googleapis.com/cuo-website/Verizon%20Logo.jpg",
  kidsuper: "https://storage.googleapis.com/cuo-website/KidSuper+Logo.png",
  arizona: "https://storage.googleapis.com/cuo-website/AriZona+Image.png",
  kiehls: "https://storage.googleapis.com/cuo-website/Kiehls+Image.png",
  nuuly: "https://storage.googleapis.com/cuo-website/Nuuly+Image.png",
  polymarket: "https://storage.googleapis.com/cuo-website/Polymarket%20Logo.png",
  kalshi: "https://storage.googleapis.com/cuo-website/Kalshi%20Logo%20Thumbnail.png",
  morgan: "https://storage.googleapis.com/cuo-website/Morgan%20and%20Morgan%20Image.jpg",
  grubhub: "https://storage.googleapis.com/cuo-website/Grubhub%20Logo.png",
} as const;

/** Same logo bank as Amex ESB About overview. */
const PARTNER_LOGOS = [
  { src: PARTNER_LOGO_ASSETS.polymarket, alt: "Polymarket", size: "large" },
  { src: PARTNER_LOGO_ASSETS.kalshi, alt: "Kalshi", size: "large" },
  { src: PARTNER_LOGO_ASSETS.morgan, alt: "Morgan & Morgan", size: "large" },
  { src: PARTNER_LOGO_ASSETS.grubhub, alt: "Grubhub", size: "large" },
  { src: "/images/brand-RedBull.png", alt: "Red Bull" },
  { src: "/images/brand-dr-squatch.png", alt: "Dr. Squatch" },
  { src: "/images/brand-skrewball.png", alt: "Skrewball", size: "huge", offsetY: 18 },
  { src: "/images/brand-wilson.png", alt: "Wilson", size: "xl" },
  { src: PARTNER_LOGO_ASSETS.verizon, alt: "Verizon", size: "large" },
  { src: "/images/brand-Malibu-logo.png", alt: "Malibu", size: "xl" },
  { src: "/images/brand-Jameson-Logo.png", alt: "Jameson" },
  { src: "/images/brand-topo_chico.png", alt: "Topo Chico" },
  { src: "/images/brand-glenlivet.png", alt: "The Glenlivet" },
  { src: "/images/brand-guayaki.png", alt: "Guayakí" },
  { src: PARTNER_LOGO_ASSETS.kidsuper, alt: "KidSuper", size: "xl" },
  { src: "/images/brand-shake-shack.png", alt: "Shake Shack", size: "xxl" },
  { src: PARTNER_LOGO_ASSETS.arizona, alt: "AriZona", size: "xxl" },
  { src: PARTNER_LOGO_ASSETS.kiehls, alt: "Kiehl's" },
  { src: PARTNER_LOGO_ASSETS.nuuly, alt: "Nuuly", size: "xl" },
  { src: "/images/brand-The-Nudge.png", alt: "The Nudge" },
] as const;

function partnerLogoDims(size?: "large" | "xl" | "xxl" | "huge") {
  if (size === "huge") {
    return { maxHeight: { xs: 140, md: 190 }, maxWidth: { xs: 320, md: 420 } };
  }
  if (size === "xxl") {
    return { maxHeight: { xs: 110, md: 150 }, maxWidth: { xs: 280, md: 360 } };
  }
  if (size === "xl") {
    return { maxHeight: { xs: 72, md: 96 }, maxWidth: { xs: 200, md: 260 } };
  }
  if (size === "large") {
    return { maxHeight: { xs: 52, md: 68 }, maxWidth: { xs: 160, md: 200 } };
  }
  return { maxHeight: { xs: 36, md: 44 }, maxWidth: { xs: 120, md: 140 } };
}

/** Same articles as the public /press page, stacked for this portal. */
const PRESS_ARTICLES = [
  {
    source: "People",
    image: "https://storage.googleapis.com/cuo-website/People%20Magazine%20Cover%20Photo.jpg",
    punchy_snippet:
      "N.Y.C. Comedy Duo Went Viral for Their Unconventional Shows. Now They Have a New Trick Up Their Sleeve (Exclusive)",
    readMoreUrl:
      "https://people.com/underground-overground-comedy-duo-announces-unexpected-new-venture-exclusive-12013357",
  },
  {
    source: "The New Yorker",
    image: "/images/press-6.webp",
    punchy_snippet:
      "Katz's Delicatessen...agreed to let a couple of twenty-four-year-olds stage a standup show among the pastrami and the Cel-Ray soda.",
    readMoreUrl: "https://www.newyorker.com/magazine/2022/12/26/two-comedians-walk-into-a-deli",
  },
  {
    source: "Deadline",
    image: "https://storage.googleapis.com/cuo-website/Deadline+Photo.jpg",
    punchy_snippet:
      "EXCLUSIVE: Underground Overground Comedy signs with WME, from one pandemic show to something much bigger.",
    readMoreUrl: "https://deadline.com/2025/08/underground-overground-comedy-signs-wme-1236495564/",
  },
  {
    source: "New York Post",
    image: "/images/paragon-stage.png",
    punchy_snippet:
      "NYC's hottest comedy show, with a 20,000-person waitlist, is in an iconic sports store",
    readMoreUrl:
      "https://nypost.com/2024/05/07/lifestyle/nyc-comedy-show-with-11k-person-waitlist-is-in-a-sports-store/",
  },
  {
    source: "Time Out",
    image: "https://storage.googleapis.com/cuo-website/Empire+State+Building+Comedy+Show.jpg",
    punchy_snippet: "The first-ever comedy show is happening inside the Empire State Building",
    readMoreUrl:
      "https://www.timeout.com/newyork/news/the-first-ever-comedy-show-is-happening-inside-the-empire-state-building-030226",
  },
  {
    source: "NBC New York",
    image: "https://storage.googleapis.com/cuo-website/New%20York%20Live%20Promo.jpg",
    punchy_snippet: "Have you ever seen stand-up comedy at a steakhouse?",
    readMoreUrl:
      "https://www.nbcnewyork.com/video/entertainment/the-scene/new-york-live/have-you-ever-seen-stand-up-comedy-at-a-steakhouse/6490982/",
  },
  {
    source: "People",
    image: "/images/press-7.webp",
    punchy_snippet:
      "Meet Two Best Friends Who Are Bringing a Pop-Up Comedy Show to N.Y.C.'s Iconic Katz's Delicatessen",
    readMoreUrl:
      "https://people.com/movies/meet-two-best-friends-who-are-bringing-a-pop-up-comedy-show-to-n-y-c-s-iconic-katzs-delicatessen/",
  },
  {
    source: "Untapped NYC",
    image: "/images/press-3.webp",
    punchy_snippet: "This Pop-Up Comedy Show Brings Laughs to Unexpected NYC Locations",
    readMoreUrl: "https://www.untappedcities.com/nyc-underground-overground-comedy/",
  },
  {
    source: "Eater",
    image: "/images/press-4.webp",
    punchy_snippet: "A Popular Underground Comedy Show Returns to Katz's Deli",
    readMoreUrl: "https://ny.eater.com/2023/10/24/23928503/katz-deli-comedy-show-november-2023",
  },
] as const;

/**
 * About ComedyUO tab for the anonymous Stadium Tour portal.
 * Does not modify the Amex portal.
 */
export default function DraftKingsAboutTab({
  onContinueToStadium,
}: {
  onContinueToStadium: () => void;
}) {
  return (
    <Box>
      <Hero onContinueToStadium={onContinueToStadium} />
      <SubNav />
      <WhoWeAre />
      <HowItWorks />
      <Demand />
      <Demographics />
      <Press />
      <Partners />
      <Bridge onContinueToStadium={onContinueToStadium} />
    </Box>
  );
}

function Hero({ onContinueToStadium }: { onContinueToStadium: () => void }) {
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: "72vh", md: "78vh" },
        display: "flex",
        alignItems: "flex-end",
        overflow: "hidden",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <MediaFill src={IMG.bestShows} alt="ComedyUO show in an iconic venue" objectPosition="center 35%" />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: {
            xs: "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.62) 40%, rgba(0,0,0,0.94) 100%)",
            md: "linear-gradient(90deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 38%, rgba(0,0,0,0.2) 68%, rgba(0,0,0,0.45) 100%), linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.94) 100%)",
          },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: { xs: "100%", md: "70%" },
          height: { xs: "70%", md: "65%" },
          background:
            "radial-gradient(ellipse at 18% 85%, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 42%, transparent 72%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pb: { xs: 5, md: 7 },
          pt: { xs: 18, md: 22 },
        }}
      >
        <Eyebrow accent>ComedyUO · Overview</Eyebrow>
        <Typography
          component="h1"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "2.35rem", md: "4rem" },
            letterSpacing: "-0.035em",
            lineHeight: 1.02,
            maxWidth: 820,
            mb: 2,
          }}
        >
          We turn iconic spaces into the world&apos;s best comedy venues.
        </Typography>
        <Typography
          sx={{
            fontFamily: portalFont,
            color: "rgba(244,245,247,0.78)",
            fontSize: { xs: "1rem", md: "1.125rem" },
            lineHeight: 1.55,
            maxWidth: 560,
            mb: { xs: 3, md: 3.5 },
          }}
        >
          This is an overview of ComedyUO, our Stadium Tour offering, and our comedy tapings. Start here to see who we are, then move through the tour and tapings.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
          <Button href="#who-we-are" sx={primaryBtnSx}>
            Meet ComedyUO
          </Button>
          <Button onClick={onContinueToStadium} sx={ghostBtnSx}>
            Skip to Stadium Tour
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function SubNav() {
  return (
    <Box
      sx={{
        position: "sticky",
        top: { xs: 64, md: 72 },
        zIndex: 30,
        bgcolor: "rgba(0,0,0,0.82)",
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          py: 1.25,
          display: "flex",
          gap: { xs: 1.5, md: 2.25 },
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {NAV.map((item) => (
          <Box
            key={item.id}
            component="a"
            href={`#${item.id}`}
            sx={{
              fontFamily: portalFont,
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: MUTED,
              whiteSpace: "nowrap",
              textDecoration: "none",
              "&:hover": { color: "#fff" },
            }}
          >
            {item.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function WhoWeAre() {
  return (
    <Section id="who-we-are">
      <Eyebrow>Who we are</Eyebrow>
      <Title>Underground Overground Comedy.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 3.5, md: 4.5 } }}>
        We transform iconic spaces into unique, after-hours comedy venues. Our founders, David and Ethan, personally vet and select each comic so guests get top-notch talent in rooms that feel impossible to recreate.
      </Body>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Photo src={IMG.marcello} alt="Marcello Hernández at Paragon" sx={{ minHeight: { xs: 240, md: 420 } }} />
        <Box sx={{ display: "grid", gap: { xs: 2, md: 2.5 } }}>
          <Photo src={IMG.chris} alt="Chris Distefano at Wilson" sx={{ minHeight: { xs: 180, md: 200 } }} />
          <Photo src={IMG.desert} alt="ComedyUO Desert 5 Spot" sx={{ minHeight: { xs: 180, md: 200 } }} />
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          borderTop: `1px solid ${LINE}`,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        {[
          { label: "Shows hosted", value: "350+" },
          { label: "Comics booked", value: "250+" },
          { label: "Waitlist", value: "39,000+" },
        ].map((stat, i) => (
          <Box
            key={stat.label}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRight: {
                xs: "none",
                sm: i < 2 ? `1px solid ${LINE}` : "none",
              },
              borderBottom: { xs: i < 2 ? `1px solid ${LINE}` : "none", sm: "none" },
            }}
          >
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, fontSize: { xs: "1.5rem", md: "1.85rem" }, letterSpacing: "-0.03em" }}>
              {stat.value}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.75rem", letterSpacing: "0.08em", textTransform: "uppercase", mt: 0.5 }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function HowItWorks() {
  const steps = [
    {
      title: "Arrival & hospitality",
      body: "Doors open into an iconic space. Drinks and food are typically served with time to settle in before the first set.",
    },
    {
      title: "The show",
      body: "At least four comics and one host, personally vetted by our founders. The room stays intimate even when the address is famous.",
    },
    {
      title: "The after",
      body: "Depending on the venue, guests stay for an afterparty, a tour or another branded moment that extends the night beyond the set.",
    },
  ];
  return (
    <Section id="how-it-works">
      <Eyebrow>How a night works</Eyebrow>
      <Title>
        Premium{" "}
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          production in unexpected rooms.
        </Box>
      </Title>
      <Body sx={{ maxWidth: 680, mb: 3 }}>
        ComedyUO handles talent, production, hospitality and the full guest journey so brands can own an ambitious live experience without building a show from scratch.
      </Body>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
        {steps.map((step, idx) => (
          <Box key={step.title} sx={{ p: 2.5, borderRadius: RADIUS, border: `1px solid ${LINE}`, bgcolor: CARD_BG }}>
            <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
              0{idx + 1}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, mb: 1 }}>{step.title}</Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.95rem", lineHeight: 1.5 }}>{step.body}</Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function Demand() {
  const [count, setCount] = useState(WAITLIST_BASE);
  useEffect(() => {
    const id = window.setInterval(() => {
      setCount((n) => n + (Math.random() > 0.7 ? 1 : 0));
    }, 4000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <Section id="demand">
      <Eyebrow>Our Waitlist</Eyebrow>
      <Title>Limited access.</Title>
      <Body sx={{ maxWidth: 640, mb: 3 }}>
        Guests can't just buy a ticket. They sign up to get on our waitlist for the chance to buy from a limited drop. That demand is what makes brand partnerships feel earned instead of bolted on.
      </Body>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: RADIUS, border: `1px solid ${LINE}`, bgcolor: CARD_BG }}>
          <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
            Live waitlist
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, fontSize: { xs: "3rem", md: "4rem" }, letterSpacing: "-0.04em", lineHeight: 1 }}>
            {count.toLocaleString()}
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: MUTED, mt: 1.5, lineHeight: 1.5 }}>
            People signed up for first access to tickets across ComedyUO nights.
          </Typography>
        </Box>
        <Photo src={IMG.crowd} alt="Audience laughing at a ComedyUO show" sx={{ minHeight: { xs: 240, md: "100%" } }} />
      </Box>
    </Section>
  );
}

function Demographics() {
  const rows = [
    { label: "Age focus", value: "Primarily 21 to 40" },
    { label: "Gender", value: "60% female" },
    { label: "Spend", value: "Premium nights out and live entertainment" },
    { label: "Discovery", value: "Instagram, YouTube, mailing list" },
  ];
  return (
    <Section id="demographics">
      <Eyebrow>Demographics</Eyebrow>
      <Title>Young, social, and ready to laugh.</Title>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mt: 1 }}>
        <Photo src={IMG.youngCrowd} alt="Young ComedyUO audience" sx={{ minHeight: { xs: 240, md: 360 } }} />
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {rows.map((row) => (
            <Box key={row.label} sx={{ p: 2.25, borderRadius: RADIUS, border: `1px solid ${LINE}`, bgcolor: CARD_BG }}>
              <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", mb: 0.75 }}>
                {row.label}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 600 }}>{row.value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
}

function Press() {
  return (
    <Section id="press">
      <Eyebrow>Press</Eyebrow>
      <Title>Don't take our word for it, take theirs.</Title>
      <Box sx={{ display: "grid", gap: 1.5, mt: 1 }}>
        {PRESS_ARTICLES.map((article) => (
          <Link
            key={`${article.source}-${article.readMoreUrl}`}
            href={article.readMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              overflow: "hidden",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
              color: "inherit",
              transition: "border-color 160ms ease, transform 160ms ease",
              "&:hover": {
                borderColor: "rgba(114,187,59,0.55)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Photo src={article.image} alt="" sx={{ minHeight: { xs: 160, sm: "100%" }, aspectRatio: { xs: "16 / 10", sm: "auto" } }} />
            <Box sx={{ p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: ACCENT,
                  mb: 0.85,
                }}
              >
                {article.source}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 600, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.35 }}>
                {article.punchy_snippet}
              </Typography>
            </Box>
          </Link>
        ))}
      </Box>
    </Section>
  );
}

function Partners() {
  return (
    <Section id="partners">
      <Eyebrow>Partners</Eyebrow>
      <Title>Brands we've worked with.</Title>
      <Body sx={{ maxWidth: 640, mb: 3 }}>
        Brands use us for access, sampling, custom shows, and content series.
      </Body>
      <Box
        sx={{
          borderRadius: RADIUS,
          bgcolor: "#fff",
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: { xs: 2.5, md: 3.5 },
          alignItems: "center",
          justifyItems: "center",
        }}
      >
        {PARTNER_LOGOS.map((logo) => {
          const dims = partnerLogoDims("size" in logo ? logo.size : undefined);
          const offsetY = "offsetY" in logo ? logo.offsetY : 0;
          return (
            <Box
              key={logo.alt}
              component="img"
              src={logo.src}
              alt={logo.alt}
              sx={{
                maxHeight: dims.maxHeight,
                maxWidth: dims.maxWidth,
                width: "auto",
                objectFit: "contain",
                display: "block",
                transform: offsetY ? `translateY(${offsetY}px)` : undefined,
              }}
            />
          );
        })}
      </Box>
    </Section>
  );
}

function Bridge({ onContinueToStadium }: { onContinueToStadium: () => void }) {
  return (
    <Section id="next">
      <Eyebrow accent>Next</Eyebrow>
      <Title>Now the stadium tour.</Title>
      <Body sx={{ maxWidth: 620, mb: 3 }}>
        With the brand picture set, the next tab walks through how ComedyUO can unlock iconic stadium spaces for intimate comedy across the country.
      </Body>
      <Button onClick={onContinueToStadium} endIcon={<SouthIcon sx={{ transform: "rotate(-90deg)", fontSize: 16 }} />} sx={primaryBtnSx}>
        Enter the Stadium Tour
      </Button>
    </Section>
  );
}

function Section({ id, children }: { id: string; children: ReactNode }) {
  return (
    <FadeUp>
      <Box
        id={id}
        component="section"
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          py: { xs: 6, md: 9 },
          scrollMarginTop: 140,
        }}
      >
        {children}
      </Box>
    </FadeUp>
  );
}

function Eyebrow({ children, accent }: { children: ReactNode; accent?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: accent ? ACCENT : MUTED,
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: portalFont,
        fontWeight: 700,
        color: "#fff",
        fontSize: "clamp(1.7rem, 2.2vw + 1rem, 2.6rem)",
        lineHeight: 1.12,
        letterSpacing: "-0.03em",
        mb: 1.5,
        maxWidth: 780,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({ children, sx }: { children: ReactNode; sx?: object }) {
  return (
    <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: { xs: "0.98rem", md: "1.05rem" }, lineHeight: 1.55, ...sx }}>
      {children}
    </Typography>
  );
}

function Photo({ src, alt, sx }: { src: string; alt: string; sx?: object }) {
  return (
    <Box sx={{ position: "relative", borderRadius: RADIUS, overflow: "hidden", border: `1px solid ${LINE}`, bgcolor: "#151515", ...sx }}>
      {src ? (
        <Box component="img" src={src} alt={alt} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <Box sx={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 30% 20%, ${ACCENT}33, transparent 55%), #171717` }} />
      )}
    </Box>
  );
}

function MediaFill({ src, alt, objectPosition = "center" }: { src: string; alt: string; objectPosition?: string }) {
  if (!src) {
    return <Box sx={{ position: "absolute", inset: 0, bgcolor: "#171717" }} aria-label={alt} />;
  }
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition }}
    />
  );
}

function FadeUp({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <Box ref={ref} sx={{ opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(16px)", transition: "opacity 650ms ease, transform 650ms ease" }}>
      {children}
    </Box>
  );
}

const primaryBtnSx = {
  fontFamily: portalFont,
  fontWeight: 600,
  textTransform: "none" as const,
  color: "#0a0a0a",
  bgcolor: "#fff",
  borderRadius: "999px",
  px: 2.5,
  py: 1,
  "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
};

const ghostBtnSx = {
  fontFamily: portalFont,
  fontWeight: 600,
  textTransform: "none" as const,
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.45)",
  borderRadius: "999px",
  px: 2.5,
  py: 1,
  "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
};
